import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { type UploadGradeBlocksResult } from 'api/grade-importer.api';

interface Props {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<UploadGradeBlocksResult>;
  onDownloadTemplate: () => Promise<Blob>;
}

interface GradeBlockPreCheck {
  totalRows: number;
  missingGradeBlockRows: number[];
}

const GRADE_BLOCK_COLUMN = 'GRADE_BLOCK';

// Mirrors the one backend rule we know for certain ("GRADE_BLOCK is required
// but was empty — row skipped") so a bad file can be caught before the
// upload round-trip, without guessing at the rest of the backend's schema.
function checkGradeBlockColumn(
  rows: Array<Record<string, unknown>>
): GradeBlockPreCheck | null {
  if (rows.length === 0) return null;
  const headerKey = Object.keys(rows[0]).find(
    (k) => k.trim().toUpperCase() === GRADE_BLOCK_COLUMN
  );
  if (!headerKey) return null;
  const missingGradeBlockRows: number[] = [];
  rows.forEach((row, idx) => {
    const value = String(row[headerKey] ?? '').trim();
    if (!value) missingGradeBlockRows.push(idx + 2);
  });
  return { totalRows: rows.length, missingGradeBlockRows };
}

export const ImportGradeV2Modal = ({
  open,
  onClose,
  onUpload,
  onDownloadTemplate,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadGradeBlocksResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [preCheck, setPreCheck] = useState<GradeBlockPreCheck | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  const reset = () => {
    setFile(null);
    setIsDragging(false);
    setUploading(false);
    setResult(null);
    setError(null);
    setDownloadingTemplate(false);
    setPreCheck(null);
    setAnalyzing(false);
  };

  const analyzeFile = (selected: File) => {
    setPreCheck(null);
    setAnalyzing(true);
    const ext = selected.name.split('.').pop()?.toLowerCase();
    const finish = (rows: Array<Record<string, unknown>>) => {
      setPreCheck(checkGradeBlockColumn(rows));
      setAnalyzing(false);
    };
    if (ext === 'csv') {
      Papa.parse<Record<string, unknown>>(selected, {
        header: true,
        skipEmptyLines: true,
        complete: (parsed) => {
          finish(parsed.data);
        },
        error: () => {
          setAnalyzing(false);
        },
      });
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const workbook = XLSX.read(e.target?.result, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          defval: '',
        });
        finish(json);
      };
      reader.onerror = () => {
        setAnalyzing(false);
      };
      reader.readAsArrayBuffer(selected);
    }
  };

  const selectFile = (selected: File) => {
    setFile(selected);
    setError(null);
    analyzeFile(selected);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) selectFile(dropped);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) selectFile(selected);
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const uploadResult = await onUpload(file);
      setResult(uploadResult);
    } catch (e) {
      const backendMessage = axios.isAxiosError(e)
        ? (e.response?.data?.message as string | undefined)
        : undefined;
      setError(backendMessage ?? t('grade_importer_v2_upload_failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadErrors = () => {
    if (!result || result.errors.length === 0) return;
    const sheet = XLSX.utils.json_to_sheet(
      result.errors.map((err) => ({
        Row: err.row,
        Error: err.message,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Errors');
    XLSX.writeFile(workbook, 'grade-blocks-import-errors.xlsx');
  };

  const handleDownloadPreCheckRows = () => {
    if (!preCheck || preCheck.missingGradeBlockRows.length === 0) return;
    const sheet = XLSX.utils.json_to_sheet(
      preCheck.missingGradeBlockRows.map((row) => ({
        Row: row,
        Issue: t('grade_importer_v2_precheck_issue'),
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Rows to fix');
    XLSX.writeFile(workbook, 'grade-blocks-precheck-warnings.xlsx');
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const blob = await onDownloadTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'grade-blocks-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (t) => alpha(t.palette.primary.main, 0.15),
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t('grade_importer_v2_title')}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
        {result ? (
          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.success.main, 0.35),
              bgcolor: (t) => alpha(t.palette.success.main, 0.04),
              p: 3,
              textAlign: 'center',
            }}
          >
            <CheckCircleOutlineIcon
              sx={{ fontSize: 40, color: 'success.main', mb: 1.5 }}
            />
            <Typography variant="body1" sx={{ mb: 2 }}>
              {result.message}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: 'center',
                mb: result.errors.length > 0 ? 2 : 0,
              }}
            >
              <Chip
                size="small"
                label={`${t('grade_importer_v2_created')}: ${
                  result.gradesCreated
                }`}
                color="success"
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${t('grade_importer_v2_updated')}: ${
                  result.gradesUpdated
                }`}
                color="info"
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${t('grade_importer_v2_failed')}: ${
                  result.gradesFailed
                }`}
                color={result.gradesFailed > 0 ? 'error' : 'default'}
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${t('grade_importer_v2_skipped')}: ${
                  result.rowsSkipped
                }`}
                color={result.rowsSkipped > 0 ? 'warning' : 'default'}
                variant="outlined"
              />
            </Box>
            {result.errors.length > 0 && (
              <Box sx={{ textAlign: 'left', mt: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontWeight: 600 }}
                  >
                    {t('grade_importer_v2_errors', {
                      count: result.errors.length,
                    })}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon fontSize="small" />}
                    onClick={handleDownloadErrors}
                    sx={{ fontWeight: 600 }}
                  >
                    {t('grade_importer_v2_download_errors')}
                  </Button>
                </Box>
                <Box
                  sx={{
                    maxHeight: 200,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {result.errors.map((err, idx) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{ color: 'error.main' }}
                    >
                      {t('grade_importer_v2_error_row', {
                        row: err.row,
                        message: err.message,
                      })}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => {
              setIsDragging(false);
            }}
            onDrop={handleDrop}
            onClick={() => {
              document.getElementById('grade-v2-file-input')?.click();
            }}
            sx={{
              border: '2px dashed',
              borderColor: (t) =>
                isDragging
                  ? t.palette.primary.main
                  : alpha(t.palette.primary.main, 0.35),
              borderRadius: 3,
              py: 6,
              px: 4,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              bgcolor: (t) =>
                isDragging
                  ? alpha(t.palette.primary.main, 0.08)
                  : alpha(t.palette.primary.main, 0.03),
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
              },
            }}
          >
            <input
              id="grade-v2-file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                border: '2px solid',
                borderColor: (t) => alpha(t.palette.primary.main, 0.3),
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 34, color: 'primary.main' }} />
            </Box>
            <Typography
              variant="h6"
              fontWeight={700}
              color="text.primary"
              gutterBottom
            >
              {t('grade_importer_v2_drop_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('grade_importer_v2_drop_body')}
            </Typography>
            {file && (
              <Chip
                icon={<InsertDriveFileOutlinedIcon />}
                label={file.name}
                onDelete={() => {
                  setFile(null);
                  setPreCheck(null);
                }}
                sx={{ fontWeight: 600 }}
              />
            )}
            {analyzing && (
              <Box sx={{ mt: 2 }}>
                <CircularProgress size={16} />
              </Box>
            )}
            {preCheck && preCheck.missingGradeBlockRows.length > 0 && (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                }}
                sx={{
                  mt: 2,
                  textAlign: 'left',
                  border: '1px solid',
                  borderColor: (t) => alpha(t.palette.warning.main, 0.35),
                  bgcolor: (t) => alpha(t.palette.warning.main, 0.06),
                  borderRadius: 2,
                  p: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <WarningAmberIcon
                    fontSize="small"
                    sx={{ color: 'warning.main', mt: 0.25 }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {t('grade_importer_v2_precheck_warning', {
                      count: preCheck.missingGradeBlockRows.length,
                      total: preCheck.totalRows,
                    })}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 0.5,
                  }}
                >
                  <Button
                    size="small"
                    startIcon={<DownloadIcon fontSize="small" />}
                    onClick={handleDownloadPreCheckRows}
                    sx={{ fontWeight: 600 }}
                  >
                    {t('grade_importer_v2_precheck_download')}
                  </Button>
                </Box>
              </Box>
            )}
            {error && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  {error}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    downloadingTemplate ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={downloadingTemplate}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleDownloadTemplate();
                  }}
                  sx={{ mt: 1.5, borderRadius: '8px', fontWeight: 600 }}
                >
                  {t('grade_importer_v2_download_template')}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {result ? (
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('grade_importer_v2_done')}
          </Button>
        ) : (
          <>
            <Button
              variant="text"
              onClick={handleClose}
              sx={{ fontWeight: 600 }}
            >
              {t('grade_importer_v2_cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={!file || uploading}
              startIcon={
                uploading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CloudUploadIcon />
                )
              }
              onClick={() => {
                void handleUpload();
              }}
              sx={{ borderRadius: '8px', fontWeight: 600 }}
            >
              {t('grade_importer_v2_upload')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
